import {
  HAZARD_DAMAGE,
  HAZARD_HEAL_RATE,
  HAZARD_SLOW_MULT,
  ROUNDS_DEFAULT,
  VISION_RADIUS,
} from "../constants.js";
import { DEFAULT_ADVANCED, HAZARD_TYPES } from "./advanced.js";
import type { AdvancedConfig, FogVisionMode, HazardType } from "./advanced.js";
import type { CtfScoreMode, FlagStealMode, GameMode, MapSize, PowerupType, WallStyle } from "./enums.js";
import { POWERUP_TYPES } from "./powerups.js";

export interface GameConfig {
  mode: GameMode;
  wallStyle: WallStyle;
  mapSize: MapSize;
  rounds: number; // round wins needed to take the match (first to N, any # of sides)
  allowLateJoin: boolean; // may players join after the match has started?
  tankSpeedPct: number; // 50..200 (% of base speed)
  hp: number; // 1..10 hits to destroy
  lives: number; // 0 = unlimited respawns; otherwise max respawns
  respawnSeconds: number; // 1..10
  killPoints: number; // points per kill
  deathPenaltyPct: number; // 0..90 (% of score lost on death)
  winScore: number; // points to win (FFA / total per team in Team VS)
  teamCount: number; // 2..4 (Team VS only)
  friendlyFire: boolean; // allow damaging yourself (any mode) and teammates (Team VS)
  teamKillPenalty: number; // points lost for killing a teammate (Team VS)
  teamSpawnZones: boolean; // Team VS: spawn each team in its own corner zone (off = randomized)
  maxFlags: number; // Capture the Flag: captures needed to win the match
  // CTF: your team carries a dropped flag back instead of it teleporting home on
  // touch; it only returns to base once a carrier brings it inside the base.
  flagTeamCarry: boolean;
  // CTF: how a flag changes hands by touch — any/team(mate)/off (kill to drop).
  flagStealMode: FlagStealMode;
  // CTF (deliver): captures a team must score to win a round (default scales with
  // team count: 1 for 2 teams, 3 for 4 teams — i.e. one per rival).
  flagsPerRound: number;
  // CTF: "deliver" = carry an enemy flag home to capture; "conquest" = hold enemy
  // flags at your base for points/sec; "carry" = a tank scores points/sec per
  // enemy flag it carries. First to winScore points takes the round (conquest/carry).
  ctfScoreMode: CtfScoreMode;
  // CTF: extra seconds added to the respawn delay on death (0 = none).
  ctfRespawnBonus: number;
  adv: AdvancedConfig; // advanced engine tuning
  // Fog of war: non-wall visuals only render inside the team's revealed areas.
  // Tanks reveal from their own position; Team VS/CTF bases and CTF flags can
  // reveal for their owning team or for everyone. The scope power-up doubles
  // tank radius and grants x-ray through walls. Client-side only — the server
  // still broadcasts all entities (a patched client could see through walls).
  fogOfWar: boolean;
  visionRadius: number; // px base sight radius (scope doubles this)
  fogBaseVision: FogVisionMode; // Team VS / CTF spawn bases: off, owning team, or all teams
  fogFlagVision: FogVisionMode; // CTF flags: off, owning team, or all teams
  // CTF: hide a flag from the fog reveal while it's being carried, so a stolen
  // flag can't be tracked across the map (you only see it with line of sight).
  fogHideCarriedFlag: boolean;
  // Hazard zones: lava/mud/ice/heal terrain tiles placed on the map.
  hazardDensity: number; // 0 = off; 1-10 zones placed on round start
  hazardTypes: HazardType[]; // enabled terrain types to include in the spawn pool
  hazardDamage: number; // lava DPS
  hazardSlowMult: number; // mud speed multiplier (0-1)
  hazardHealRate: number; // heal HP per second
  // Destructible walls: internal walls have HP and can be breached by bullets.
  // Border walls are always indestructible.
  destructibleWalls: boolean;
  // Power-ups
  powerups: boolean; // spawn pickups on the map
  powerupEverySeconds: number; // spawn cadence
  powerupDespawnSeconds: number; // uncollected pickups vanish after this
  powerupCharges: number; // uses granted per pickup (weapon types)
  powerupSpawnCount: number; // crates spawned together each time the spawn cadence ticks (no cap on concurrent crates — despawn bounds them)
  powerupTypes: PowerupType[]; // enabled power-up types to include in the spawn pool
  powerupStacking: boolean; // same-type pickups stack (weapons add charges, buffs add duration) instead of replacing
  combineWeapons: boolean; // hold multiple weapon effects at once; each shot composes them all (laser stays exclusive)
  tankCollision: boolean; // circle-to-circle pushing (FFA only)
  radar: boolean; // HUD radar that pings nearby tanks (host toggle for all)
}

export const DEFAULT_GAME_CONFIG: GameConfig = {
  mode: "ffa",
  wallStyle: "maze",
  mapSize: "random",
  rounds: ROUNDS_DEFAULT,
  allowLateJoin: true,
  tankSpeedPct: 100,
  hp: 1,
  lives: 0,
  respawnSeconds: 3,
  killPoints: 60,
  deathPenaltyPct: 25,
  winScore: 300,
  teamCount: 2,
  friendlyFire: false,
  teamKillPenalty: 60,
  teamSpawnZones: true,
  maxFlags: 3,
  flagTeamCarry: true,
  flagStealMode: "any",
  flagsPerRound: 1,
  ctfScoreMode: "deliver",
  ctfRespawnBonus: 3,
  adv: DEFAULT_ADVANCED,
  fogOfWar: false,
  visionRadius: VISION_RADIUS,
  fogBaseVision: "team",
  fogFlagVision: "team",
  fogHideCarriedFlag: false,
  hazardDensity: 0,
  hazardTypes: [...HAZARD_TYPES],
  hazardDamage: HAZARD_DAMAGE,
  hazardSlowMult: HAZARD_SLOW_MULT,
  hazardHealRate: HAZARD_HEAL_RATE,
  destructibleWalls: false,
  powerups: true,
  powerupEverySeconds: 8,
  powerupDespawnSeconds: 12,
  powerupCharges: 1,
  powerupSpawnCount: 1,
  powerupTypes: [...POWERUP_TYPES],
  powerupStacking: true,
  combineWeapons: false,
  tankCollision: false,
  radar: true,
};

export type GameConfigInput = Partial<Omit<GameConfig, "adv">> & { adv?: Partial<AdvancedConfig> };

/** Fill any missing config fields from the shared defaults. This is intentionally
 *  not a trust-boundary sanitizer; the server still clamps raw client input. */
export function gameConfigWithDefaults(config: GameConfigInput = {}): GameConfig {
  return {
    ...DEFAULT_GAME_CONFIG,
    ...config,
    adv: { ...DEFAULT_ADVANCED, ...(config.adv ?? {}) },
    hazardTypes: config.hazardTypes ? [...config.hazardTypes] : [...DEFAULT_GAME_CONFIG.hazardTypes],
    powerupTypes: config.powerupTypes ? [...config.powerupTypes] : [...DEFAULT_GAME_CONFIG.powerupTypes],
  };
}
